import React, { useState, useCallback, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import useProduct from "../../hooks/useProduct";
import { hasImage } from "../Products/components/ProductItem";
import Loader from "../../components/Loader";
import { formatCurrency } from "../../util/formatters";
import useCart from "../../hooks/useCart";
import Slider from "react-slick";
import SEO from "../../components/SEO";

function NextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, borderRadius: "20px", background: "grey" }}
      onClick={onClick}
    />
  );
}

function PrevArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, borderRadius: "20px", background: "grey" }}
      onClick={onClick}
    />
  );
}

const ProductDetail = () => {
  const params = useParams();
  // Mantém como string para permitir edição livre (ex: apagar/0) enquanto digita.
  const [quantity, setQuantity] = useState("1");
  const { products, productFilters } = useProduct();
  const { dispatch } = useCart();

  const settings = {
    className: {
      display: "flex",
      justifyContent: "center",
      flexDirection: "row",
    },
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const quantityHandler = (e) => {
    const raw = e.target.value;
    // aceita vazio durante edição, e remove tudo que não for dígito
    const digitsOnly = String(raw).replace(/\D/g, "");
    setQuantity(digitsOnly);
  };

  const normalizeQuantity = () => {
    const n = parseInt(quantity, 10);
    if (!Number.isFinite(n) || n <= 0) {
      setQuantity("1");
      return 1;
    }
    setQuantity(String(n));
    return n;
  };

  const cartHandler = useCallback(
    (obj) => {
      dispatch({
        type: "ADD",
        obj,
        quantity,
      });
    },
    [dispatch, quantity],
  );

  const product = () => {
    if (params.id && products) {
      return products.find((e) => e.id === params.id);
    }
    return null;
  };

  const productCategory = () => {
    if (product() && productFilters.length !== 0) {
      const value = productFilters.find(
        (e) => e.id === parseInt(product().category),
      );
      return value?.name;
    }
    return null;
  };

  const addToCart = () => {
    const normalizedQty = normalizeQuantity();
    const obj = {
      ...product(),
      quantity: normalizedQty,
    };
    cartHandler(obj);
  };

  const getYouTubeEmbed = (youtubeUrl) => {
    if (!youtubeUrl) return null;
    // Se já é uma URL de embed do YouTube (youtube.com/embed/...), usa direto
    if (youtubeUrl.includes("youtube.com/embed/")) {
      const embedUrl = youtubeUrl.trim();
      return `<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; border-radius: 8px;"><iframe style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
    }

    // Extrai o ID do vídeo do YouTube de diferentes formatos de URL
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    const youtubeMatch = youtubeUrl.match(youtubeRegex);

    if (youtubeMatch) {
      const videoId = youtubeMatch[1];
      return `<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; border-radius: 8px;"><iframe style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
    }

    // Se não for reconhecido, retorna null
    return null;
  };

  const currentProduct = product();
  const productTitle = currentProduct?.product_name
    ? `${currentProduct.product_name} - ${currentProduct.product_id} | Grupo Terra`
    : "Detalhes do Produto | Grupo Terra";
  const productDescription = currentProduct?.description
    ? `${currentProduct.product_name} - ${currentProduct.description.substring(0, 155)}...`
    : "Visualize detalhes completos do produto, especificações técnicas e informações para sua oficina de mecânica pesada.";

  // Structured Data para Product
  useEffect(() => {
    if (!currentProduct) return;

    let productScript = document.querySelector('script[data-jsonld="product"]');
    if (!productScript) {
      productScript = document.createElement("script");
      productScript.setAttribute("type", "application/ld+json");
      productScript.setAttribute("data-jsonld", "product");
      document.head.appendChild(productScript);
    }

    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: currentProduct.product_name,
      description: currentProduct.description || productDescription,
      image: currentProduct.image ? hasImage(currentProduct.image) : undefined,
      sku: currentProduct.product_id?.toString(),
      brand: {
        "@type": "Brand",
        name: "Grupo Terra",
      },
      offers: {
        "@type": "Offer",
        price: currentProduct.price?.toString(),
        priceCurrency: "BRL",
        availability: "https://schema.org/InStock",
        url: `${window.location.origin}/produto/${currentProduct.id}`,
      },
    };

    // Adiciona vídeo se existir
    if (currentProduct.youtube_link) {
      const videoUrl = currentProduct.youtube_link;
      const youtubeRegex =
        /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
      const youtubeMatch = videoUrl.match(youtubeRegex);

      if (youtubeMatch) {
        productSchema.video = {
          "@type": "VideoObject",
          name: `${currentProduct.product_name} - Vídeo demonstrativo`,
          description:
            currentProduct.complementary_description ||
            `Vídeo demonstrativo do produto ${currentProduct.product_name}`,
          thumbnailUrl: `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`,
          uploadDate: new Date().toISOString(),
          contentUrl: videoUrl,
          embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
        };
      }
    }

    productScript.textContent = JSON.stringify(productSchema);

    // BreadcrumbList
    let breadcrumbScript = document.querySelector(
      'script[data-jsonld="breadcrumb"]',
    );
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement("script");
      breadcrumbScript.setAttribute("type", "application/ld+json");
      breadcrumbScript.setAttribute("data-jsonld", "breadcrumb");
      document.head.appendChild(breadcrumbScript);
    }

    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Página Inicial",
          item: window.location.origin,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Produtos",
          item: `${window.location.origin}/produtos`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: currentProduct.product_name,
          item: `${window.location.origin}/produto/${currentProduct.id}`,
        },
      ],
    });

    return () => {
      // Cleanup ao desmontar
      const scripts = document.querySelectorAll(
        'script[data-jsonld="product"], script[data-jsonld="breadcrumb"]',
      );
      scripts.forEach((script) => script.remove());
    };
  }, [currentProduct, productDescription]);

  return (
    <>
      <SEO
        title={productTitle}
        description={productDescription}
        image={
          currentProduct?.image ? hasImage(currentProduct.image) : undefined
        }
        type="product"
      />
      <div className="container" style={{ marginBottom: "100px" }}>
        {!products ? (
          <Loader />
        ) : (
          <>
            <div className="container title-2">
              <nav aria-label="Breadcrumb"  style={{fontSize: "1rem"}}>
                <ol
                  style={{
                    listStyle: "none",
                    padding: 0,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    alignItems: "center",
                  }}
                  itemScope
                  itemType="https://schema.org/BreadcrumbList"
                >
                  <li
                    itemProp="itemListElement"
                    itemScope
                    itemType="https://schema.org/ListItem"
                  >
                    <Link to="/" itemProp="item" title="Página inicial">
                      <span itemProp="name">Página Inicial</span>
                    </Link>
                    <meta itemProp="position" content="1" />
                  </li>
                  <li aria-hidden="true">/</li>
                  <li
                    itemProp="itemListElement"
                    itemScope
                    itemType="https://schema.org/ListItem"
                  >
                    <Link
                      to="/produtos"
                      style={{ color: "inherit", textDecoration: "none" }}
                      itemProp="item"
                      title="Produtos"
                    >
                      <span itemProp="name">Produtos</span>
                    </Link>
                    <meta itemProp="position" content="2" />
                  </li>
                  <li aria-hidden="true">/</li>
                  <li
                    itemProp="itemListElement"
                    itemScope
                    itemType="https://schema.org/ListItem"
                    aria-current="page"
                  >
                    <span itemProp="name">{currentProduct?.product_name}</span>
                    <meta itemProp="position" content="3" />
                  </li>
                </ol>
              </nav>
              <h1 className="ltn__primary-color" style={{ fontSize: "2rem" }}>
                {currentProduct?.product_name}
              </h1>
              <h2
                className="ltn__primary-color"
                style={{ fontSize: "1rem", fontWeight: "normal" }}
              >
                Código: {currentProduct?.product_id}
              </h2>
            </div>
            <div className="row row-cols-lg-2 row-cols-sm-1">
              <div className="col-lg-6 col-12 mb-4 mb-lg-0">
                {product().images_details.length === 0 ? (
                  <div
                    style={{
                      marginBottom: "20px",
                      marginTop: "20px",
                      paddingLeft: "15px",
                      paddingRight: "15px",
                    }}
                    className="px-lg-5"
                  >
                    <img
                      src={hasImage(product()?.image)}
                      alt={
                        product()?.product_name
                          ? `${product().product_name} - ${product().product_id} | Grupo Terra`
                          : "Imagem do produto"
                      }
                      style={{
                        border: "4px solid #F2F6F7",
                        borderRadius: "20px",
                        width: "100%",
                        height: "auto",
                        maxHeight: "500px",
                        objectFit: "contain",
                      }}
                      loading="lazy"
                      fetchPriority="high"
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      marginBottom: "20px",
                      paddingLeft: "15px",
                      paddingRight: "15px",
                    }}
                    className="px-lg-5"
                  >
                    <Slider {...settings}>
                      {product().images_details.map((e) => (
                        <div key={e.url_image}>
                          <img
                            src={hasImage(e.url_image)}
                            alt={
                              product()?.product_name
                                ? `${product().product_name} - Imagem ${product().images_details.indexOf(e) + 1}`
                                : "Imagem do produto"
                            }
                            style={{
                              border: "4px solid #F2F6F7",
                              borderRadius: "20px",
                              width: "100%",
                              height: "auto",
                              maxHeight: "500px",
                              objectFit: "contain",
                            }}
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </Slider>
                  </div>
                )}
              </div>
              <div className="col-lg-6 col-12">
                <div
                  style={{
                    background: "#F2F6F7",
                    minHeight: "300px",
                    marginTop: "20px",
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                  }}
                  className="p-lg-5"
                >
                  <h2
                    style={{
                      color: "#000",
                      marginBottom: "15px",
                      fontSize: "1.5rem",
                    }}
                  >{`Categoria: ${productCategory()}`}</h2>
                  <div style={{ margin: 0 }}>
                    {product().description ? (
                      <p style={{ margin: 0 }}>{product().description}</p>
                    ) : (
                      <p
                        style={{
                          margin: 0,
                          fontStyle: "italic",
                          color: "#999",
                        }}
                      >
                        Produto ainda sem descrição. Entre em contato para mais
                        informações.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                marginBottom: "20px",
                marginTop: "20px",
                overflowX: "auto",
              }}
            >
              {currentProduct?.promotional_payment_methods?.length > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    marginBottom: "12px",
                    padding: "10px 14px",
                    backgroundColor: "rgba(25, 135, 84, 0.1)",
                    border: "1px solid var(--bs-success, #198754)",
                    borderRadius: "8px",
                    fontSize: "0.95rem",
                  }}
                >
                  <strong
                    style={{ color: "var(--bs-success, #198754)" }}
                  >
                    <i
                      className="fa-solid fa-tag"
                      style={{ marginRight: "6px" }}
                    ></i>
                    Forma de pagamento promocional
                  </strong>
                  <p style={{ margin: "6px 0 0", color: "#333" }}>
                    {currentProduct.promotional_payment_methods
                      .map((m) =>
                        m.installments && m.days
                          ? `${m.payment_name} (${m.installments}x em ${m.days} dias)`
                          : m.payment_name,
                      )
                      .join(" • ")}
                  </p>
                </div>
              )}
              <div>
                <h2
                  className="ltn__secondary-color"
                  style={{ fontSize: "1.75rem", marginBottom: "15px" }}
                >
                  Especificações
                </h2>
              </div>
              <div style={{ overflowX: "auto", width: "100%" }}>
                <table
                  style={{
                    borderCollapse: "collapse",
                    width: "100%",
                    minWidth: "600px",
                  }}
                >
                  <thead>
                    <tr style={{ border: "none" }}>
                      <td
                        className="ltn__primary-color"
                        style={{
                          paddingTop: "10px",
                          paddingBottom: "10px",
                          paddingLeft: "10px",
                          paddingRight: "10px",
                        }}
                      >
                        <b>Código do produto</b>
                      </td>
                      <td
                        className="ltn__primary-color"
                        style={{ padding: "10px" }}
                      >
                        <b>Valor</b>
                      </td>
                      <td
                        className="ltn__primary-color"
                        style={{ padding: "10px" }}
                      >
                        <b>Quantidade</b>
                      </td>
                      <td
                        className="ltn__primary-color"
                        style={{ padding: "10px" }}
                      >
                        <b>Valor Total</b>
                      </td>
                      <td
                        className="ltn__primary-color"
                        style={{ padding: "10px", textAlign: "end" }}
                      >
                        <b>Ação</b>
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ textAlign: "start", background: "#F2F6F7" }}>
                      <td style={{ padding: "10px" }}>
                        {product()?.product_id}
                      </td>
                      <td style={{ padding: "10px" }}>
                        {formatCurrency(product()?.price)}
                      </td>
                      <td style={{ textAlign: "start", padding: "10px" }}>
                        <input
                          min={1}
                          value={quantity}
                          onChange={quantityHandler}
                          onFocus={(e) => e.target.select()}
                          onBlur={normalizeQuantity}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          style={{
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            width: "80px",
                            padding: "6px",
                            height: "45.6px",
                          }}
                        />
                      </td>
                      <td style={{ textAlign: "start", padding: "10px" }}>
                        {formatCurrency(
                          product()?.price *
                            (parseInt(quantity || "0", 10) || 0)
                        )}
                      </td>
                      <td
                        style={{
                          textAlign: "end",
                          padding: "10px",
                        }}
                      >
                        <button
                          className="btn-small theme-btn-1 btn-effect-1"
                          onClick={addToCart}
                          style={{ whiteSpace: "nowrap" }}
                          aria-label={`Adicionar ${product()?.product_name ?? "produto"} ao carrinho`}
                          title={`Adicionar ${product()?.product_name ?? "produto"} ao carrinho`}
                          type="button"
                        >
                          <i
                            className="fa-solid fa-cart-shopping"
                            aria-hidden="true"
                          ></i>
                          <span className="d-none d-md-inline">
                            {" "}
                            Adicionar ao carrinho
                          </span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            {(() => {
              const currentProduct = product();
              if (!currentProduct) return null;

              const youtubeLink = currentProduct.youtube_link;
              const complementaryDesc =
                currentProduct.complementary_description;

              const hasYoutube =
                youtubeLink && String(youtubeLink).trim().length > 0;
              const hasComplementary =
                complementaryDesc &&
                String(complementaryDesc).trim().length > 0;

              if (!hasYoutube && !hasComplementary) return null;

              return (
                <div style={{ marginBottom: "20px", marginTop: "40px" }}>
                  <div>
                    <h2
                      className="ltn__secondary-color"
                      style={{ fontSize: "1.75rem", marginBottom: "15px" }}
                    >
                      Materiais Complementares
                    </h2>
                  </div>
                  <div style={{ marginTop: "20px" }}>
                    {hasYoutube &&
                      (() => {
                        const embedHtml = getYouTubeEmbed(youtubeLink);
                        return embedHtml ? (
                          <div style={{ marginBottom: "20px" }}>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: embedHtml,
                              }}
                            />
                          </div>
                        ) : null;
                      })()}
                    {hasComplementary && (
                      <div
                        style={{
                          background: "#F2F6F7",
                          padding: "20px",
                          borderRadius: "8px",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <div style={{ margin: 0, color: "#000" }}>
                          {String(complementaryDesc).trim()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </>
  );
};
export default ProductDetail;
